import React, { useState, useMemo, useRef, useCallback } from 'react';
import { cn } from '../../lib/utils';
import { IconPlus } from '../shared/icons';

const SORT_OPTIONS = [
  { key: 'recent',  label: 'Recent' },
  { key: 'oldest',  label: 'Oldest' },
  { key: 'name',    label: 'A → Z' },
  { key: 'name-desc', label: 'Z → A' },
  { key: 'slides',  label: 'Slides' },
];

function sortCarousels(list, sortKey) {
  const copy = [...list];
  switch (sortKey) {
    case 'oldest':
      return copy.sort((a, b) => (a.updated_at || '').localeCompare(b.updated_at || ''));
    case 'name':
      return copy.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    case 'name-desc':
      return copy.sort((a, b) => (b.name || '').localeCompare(a.name || ''));
    case 'slides':
      return copy.sort((a, b) => (b.slides?.length || 0) - (a.slides?.length || 0));
    case 'recent':
    default:
      return copy.sort((a, b) => (b.updated_at || '').localeCompare(a.updated_at || ''));
  }
}

/**
 * Carousel list component with card-style tiles, LED indicators,
 * sorting controls, inline rename, and inline delete confirmation.
 */
export function CarouselList({
  carousels,
  activeId,
  onSelect,
  onCreate,
  onRename,
  onDelete,
  isLoading = false,
  isCreating = false,
}) {
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [sortKey, setSortKey] = useState('recent');
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [popupPos, setPopupPos] = useState({ top: 0, left: 0 });

  const containerRef = useRef(null);

  const sorted = useMemo(() => sortCarousels(carousels, sortKey), [carousels, sortKey]);

  const handleStartRename = (carousel, e) => {
    e.stopPropagation();
    setPendingDeleteId(null);
    setEditingId(carousel.id);
    setEditName(carousel.name);
  };

  const handleFinishRename = () => {
    if (editingId && editName.trim()) {
      onRename(editingId, editName.trim());
    }
    setEditingId(null);
    setEditName('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleFinishRename();
    } else if (e.key === 'Escape') {
      setEditingId(null);
      setEditName('');
    }
  };

  const handleDeleteClick = useCallback((id, e) => {
    e.stopPropagation();
    if (carousels.length <= 1) return;

    // Find the button element (walk up from the badge to the .carousel-item)
    const badge = e.currentTarget;
    const btn = badge.closest('.carousel-item');
    const container = containerRef.current;

    if (btn && container) {
      const containerRect = container.getBoundingClientRect();
      const btnRect = btn.getBoundingClientRect();
      const popupW = 280;

      // Center popup horizontally under the button
      let left = (btnRect.left + btnRect.width / 2) - containerRect.left - popupW / 2;
      // Clamp so it doesn't overflow left or right
      const maxLeft = containerRect.width - popupW;
      left = Math.max(0, Math.min(left, maxLeft));

      setPopupPos({
        top: btnRect.bottom - containerRect.top + 4,
        left,
      });
    }

    setPendingDeleteId(id);
  }, [carousels.length]);

  const handleDeleteConfirm = () => {
    if (pendingDeleteId) {
      onDelete(pendingDeleteId);
    }
    setPendingDeleteId(null);
  };

  const handleDeleteCancel = (e) => {
    if (e) e.stopPropagation();
    setPendingDeleteId(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-xs text-white/50 py-2">
        <div className="animate-spin h-4 w-4 border-2 border-white/30 border-t-white/70 rounded-full" />
        Loading carousels...
      </div>
    );
  }

  const pendingCarousel = pendingDeleteId ? carousels.find(c => c.id === pendingDeleteId) : null;

  return (
    <div className="relative" ref={containerRef}>
      {/* Header row: label + sort pills */}
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-xs text-white/50 uppercase tracking-wider">Carousels</div>
        <div className="flex items-center gap-1">
          {SORT_OPTIONS.map(opt => {
            const isActive = sortKey === opt.key;
            return (
              <button
                key={opt.key}
                onClick={() => setSortKey(opt.key)}
                className={cn(
                  'flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold tracking-wide transition-colors',
                  isActive
                    ? 'text-white/80'
                    : 'text-white/25 hover:text-white/50'
                )}
              >
                <span className={cn(
                  'inline-block w-[3px] h-[3px] rounded-full flex-shrink-0 transition-all',
                  isActive
                    ? 'bg-green-300 shadow-[0_0_3px_1px_rgba(134,239,172,0.3)]'
                    : 'bg-white/[0.06]'
                )} />
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Carousel items — instrument-button style, no recessed backing */}
      <div className="flex flex-wrap gap-2">
          {sorted.map((carousel) => {
            const isActive = carousel.id === activeId;
            const isEditing = carousel.id === editingId;

            return (
              <button
                key={carousel.id}
                onClick={() => !isEditing && onSelect(carousel.id)}
                className={cn(
                  'group carousel-item',
                  isActive ? 'carousel-item-active' : 'carousel-item-inactive',
                  pendingDeleteId === carousel.id && 'carousel-item-pending-delete'
                )}
              >
                <span className="carousel-led" />

                {!isEditing && carousels.length > 1 && pendingDeleteId !== carousel.id && (
                  <span
                    onClick={(e) => handleDeleteClick(carousel.id, e)}
                    className="corner-badge corner-badge-tr corner-badge-sm corner-badge-delete corner-badge-hover"
                    title="Delete carousel"
                    role="button"
                    aria-label={`Delete ${carousel.name}`}
                  >
                    <svg width="7" height="7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round">
                      <path d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </span>
                )}

                {isEditing ? (
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onBlur={handleFinishRename}
                    onKeyDown={handleKeyDown}
                    onClick={(e) => e.stopPropagation()}
                    className="bg-transparent border-none outline-none w-36 text-[11px] font-bold text-white/90"
                    autoFocus
                  />
                ) : (
                  <span
                    onDoubleClick={(e) => handleStartRename(carousel, e)}
                    className="max-w-[140px] truncate"
                  >
                    {carousel.name}
                  </span>
                )}
              </button>
            );
          })}

          {/* Add carousel button — instrument style */}
          <button
            onClick={onCreate}
            disabled={isCreating}
            className="carousel-item carousel-item-inactive"
            title="New carousel"
            style={{ gap: '4px' }}
          >
            {isCreating ? (
              <span className="animate-spin h-3 w-3 border-[1.5px] border-white/20 border-t-white/60 rounded-full" />
            ) : (
              <IconPlus className="h-3 w-3" style={{ opacity: 0.5 }} />
            )}
            <span>{isCreating ? 'Creating…' : 'New'}</span>
          </button>
      </div>

      {/* Inline delete confirmation — anchored below the clicked button */}
      {pendingDeleteId && pendingCarousel && (
        <div
          className="absolute z-20 w-[280px] bg-[#1a1214] border border-red-500/15 rounded-lg px-2.5 py-2.5 shadow-lg shadow-black/40"
          style={{ top: popupPos.top, left: popupPos.left }}
        >
          <div className="flex items-start gap-2 text-[11px] text-red-400/80">
            <svg className="w-3.5 h-3.5 flex-shrink-0 mt-[1px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span>
              Delete <strong>{pendingCarousel.name}</strong>? This cannot be undone.
            </span>
          </div>
          <div className="flex gap-2 mt-2.5 ml-5">
            <button
              onClick={handleDeleteConfirm}
              className="px-3 py-1 text-[11px] font-medium rounded-md bg-red-500/20 text-red-300 hover:bg-red-500/30 transition-colors"
            >
              Delete
            </button>
            <button
              onClick={handleDeleteCancel}
              className="px-3 py-1 text-[11px] font-medium rounded-md bg-white/[0.04] text-white/50 hover:bg-white/[0.08] hover:text-white/70 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="mt-1.5 text-[10px] text-white/30">
        Double-click to rename
      </div>
    </div>
  );
}
